import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Delete, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import OwnerPinResetDialog from "@/components/OwnerPinResetDialog";
import { useKeypadKeys } from "@/hooks/useKeypadKeys";

export default function ManagerPinModal({ open, onClose, onSubmit, title = "Manager Approval Required", reason = "" }) {
  const { user } = useAuth();
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgot, setForgot] = useState(false);

  const press = (d) => { setError(""); setPin((p) => (p.length < 6 ? p + d : p)); };
  const back = () => setPin((p) => p.slice(0, -1));
  const clear = () => setPin("");

  const submit = async () => {
    if (loading) return;
    if (pin.length < 4) { setError("Enter a 4-6 digit PIN"); return; }
    setLoading(true);
    try {
      await onSubmit(pin);
      setPin("");
    } catch (e) {
      setPin("");
      setError(e?.message || "Invalid PIN");
    } finally {
      setLoading(false);
    }
  };

  // Core UX standard: physical keyboard / numpad drive the keypad while the modal is open
  // (paused while the Forgot PIN dialog is on top so its inputs type normally).
  useKeypadKeys({
    active: open && !forgot && !loading,
    onDigit: press, onBackspace: back, onClear: clear, onEnter: submit,
    onEscape: () => { setPin(""); setError(""); onClose(); },
  });

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setPin(""); setError(""); onClose(); } }}>
      <DialogContent className="max-w-sm" data-testid="manager-pin-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-head">
            <span className="p-1.5 rounded-lg bg-violet-500/15 text-violet-500"><ShieldCheck className="w-5 h-5" /></span>
            {title}
          </DialogTitle>
          <DialogDescription>{reason || "Enter a Manager or Owner PIN to authorize this action."} <span className="text-[11px] opacity-70">Type on your keyboard or numpad · Enter to authorize · Esc to cancel</span></DialogDescription>
        </DialogHeader>
        <div className="flex justify-center gap-2 my-3">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className={`w-6 h-8 rounded-md border-2 flex items-center justify-center mono text-lg
              ${i < pin.length ? "border-violet-500 bg-violet-500/10" : "border-border"}`}>
              {i < pin.length ? "•" : ""}
            </div>
          ))}
        </div>
        {error && <p className="text-center text-sm text-rose-500 -mt-1" data-testid="pin-error">{error}</p>}
        <div className="grid grid-cols-3 gap-2">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
            <Button key={d} variant="outline" className="h-14 text-xl mono"
              data-testid={`pin-key-${d}`} onClick={() => press(String(d))}>{d}</Button>
          ))}
          <Button variant="outline" className="h-14" onClick={back} data-testid="pin-back"><Delete className="w-5 h-5" /></Button>
          <Button variant="outline" className="h-14 text-xl mono" data-testid="pin-key-0" onClick={() => press("0")}>0</Button>
          <Button variant="ghost" className="h-14" onClick={() => { setPin(""); setError(""); onClose(); }}><X className="w-5 h-5" /></Button>
        </div>
        <Button className="w-full h-12 mt-1 bg-violet-600 hover:bg-violet-700 text-white"
          disabled={loading} onClick={submit} data-testid="pin-submit">
          {loading ? "Verifying..." : "Authorize"}
        </Button>
        {user?.role === "owner" && (
          <button type="button" className="text-xs text-violet-500 hover:underline w-full text-center mt-1" onClick={() => setForgot(true)} data-testid="forgot-pin-link">Forgot PIN?</button>
        )}
      </DialogContent>
      <OwnerPinResetDialog open={forgot} onClose={() => setForgot(false)} onDone={() => { setPin(""); setError(""); }} />
    </Dialog>
  );
}
